import type { CourseListItemDto, CoursePhase } from './types';
import type {
  BlackBrowseFamilySlug,
  WhiteRepertoireCollection,
} from 'endchess-contracts';
import { isBlackBrowseFamilySlug } from 'endchess-contracts';

const COURSES_ROOT = '/courses';

export const WHITE_COLLECTION_HUB_SLUG_PREFIX = 'white-hub-' as const;
export const BLACK_FAMILY_HUB_SLUG_PREFIX = 'black-hub-' as const;

export const whiteCollectionHubSlug = (
  collection: WhiteRepertoireCollection,
): string => `${WHITE_COLLECTION_HUB_SLUG_PREFIX}${collection}`;

export const blackFamilyHubSlug = (familySlug: BlackBrowseFamilySlug): string =>
  `${BLACK_FAMILY_HUB_SLUG_PREFIX}${familySlug}`;

export const parseWhiteCollectionHubSlug = (
  slug: string,
): WhiteRepertoireCollection | null => {
  if (slug === whiteCollectionHubSlug('e4')) {
    return 'e4';
  }
  if (slug === whiteCollectionHubSlug('d4')) {
    return 'd4';
  }
  return null;
};

export const parseBlackFamilyHubSlug = (
  slug: string,
): BlackBrowseFamilySlug | null => {
  if (!slug.startsWith(BLACK_FAMILY_HUB_SLUG_PREFIX)) {
    return null;
  }
  const familySlug = slug.slice(BLACK_FAMILY_HUB_SLUG_PREFIX.length);
  return isBlackBrowseFamilySlug(familySlug) ? familySlug : null;
};

export type CourseTrainScope =
  | { kind: 'global' }
  | { kind: 'phase'; phase: CoursePhase }
  | { kind: 'opening-side'; trainSide: 'b' }
  | { kind: 'white-collection'; collection: WhiteRepertoireCollection }
  | { kind: 'black-family'; familySlug: BlackBrowseFamilySlug }
  | { kind: 'course'; phase: CoursePhase; courseSlug: string };

/** Middlegame/endgame/mistakes skip the per-part course picker and show lessons directly. */
export const phaseSkipsCourseList = (phase: CoursePhase): boolean =>
  phase === 'middlegame' || phase === 'endgame' || phase === 'mistakes';

export const coursesRootPath = (): string => COURSES_ROOT;

export const courseBrowsePath = (
  phase: CoursePhase,
  courseSlug: string,
): string => courseDetailPath(phase, courseSlug);

export const coursePhasePath = (phase: CoursePhase): string =>
  `${COURSES_ROOT}/${phase}`;

export const courseDetailPath = (
  phase: CoursePhase,
  courseSlug: string,
): string =>
  `${COURSES_ROOT}/${phase}/${encodeURIComponent(courseSlug)}`;

export const courseLessonPath = (
  phase: CoursePhase,
  courseSlug: string,
  lessonId: string,
): string =>
  `${courseDetailPath(phase, courseSlug)}/lessons/${encodeURIComponent(lessonId)}`;

/** Card link for a catalog course or synthetic opening hub (e4/d4 / black family). */
export const courseCardPath = (
  course: Pick<CourseListItemDto, 'phase' | 'slug' | 'lastLessonId'>,
  linkToLastLesson = false,
): string => {
  const whiteCollection = parseWhiteCollectionHubSlug(course.slug);
  if (whiteCollection) {
    return whiteCollectionPath(whiteCollection);
  }
  const blackFamily = parseBlackFamilyHubSlug(course.slug);
  if (blackFamily) {
    return blackFamilyPath(blackFamily);
  }
  if (linkToLastLesson && course.lastLessonId) {
    return courseLessonPath(course.phase, course.slug, course.lastLessonId);
  }
  return courseDetailPath(course.phase, course.slug);
};

export const coursesTrainAllPath = (): string => `${COURSES_ROOT}/train`;

export const phaseTrainPath = (phase: CoursePhase): string =>
  `${coursePhasePath(phase)}/train`;

export const openingSidePath = (trainSide: 'w' | 'b'): string =>
  `${COURSES_ROOT}/opening/${trainSide}`;

export const openingSideTrainPath = (trainSide: 'b'): string =>
  `${openingSidePath(trainSide)}/train`;

export const parseOpeningSideParam = (
  value: string | undefined,
): 'w' | 'b' | null => {
  if (value === 'w' || value === 'b') {
    return value;
  }
  return null;
};

export const whiteCollectionPath = (
  collection: WhiteRepertoireCollection,
): string => `${COURSES_ROOT}/opening/w/${collection}`;

export const whiteCollectionTrainPath = (
  collection: WhiteRepertoireCollection,
): string => `${whiteCollectionPath(collection)}/train`;

export const blackFamilyPath = (familySlug: string): string =>
  `${COURSES_ROOT}/opening/b/${encodeURIComponent(familySlug)}`;

export const blackFamilyTrainPath = (familySlug: string): string =>
  `${blackFamilyPath(familySlug)}/train`;

export const courseTrainAllPath = (
  phase: CoursePhase,
  courseSlug: string,
): string =>
  `${courseDetailPath(phase, courseSlug)}/train`;

export const parseWhiteCollectionParam = (
  value: string | undefined,
): WhiteRepertoireCollection | null => {
  if (value === 'e4' || value === 'd4') {
    return value;
  }
  return null;
};

export const parseBlackFamilyParam = (
  value: string | undefined,
): string | null => {
  if (!value) {
    return null;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const parseCoursePhaseParam = (
  value: string | undefined,
): CoursePhase | null => {
  if (!value) {
    return null;
  }
  if (value === 'opening' || value === 'middlegame' || value === 'endgame' || value === 'mistakes') {
    return value;
  }
  return null;
};

export const parseCourseTrainScope = (pathname: string): CourseTrainScope => {
  const whiteCollectionTrain = /^\/courses\/opening\/w\/(e4|d4)\/train$/.exec(pathname);
  if (whiteCollectionTrain) {
    return {
      kind: 'white-collection',
      collection: whiteCollectionTrain[1] as WhiteRepertoireCollection,
    };
  }

  const blackFamilyTrain = /^\/courses\/opening\/b\/([^/]+)\/train$/.exec(pathname);
  if (blackFamilyTrain) {
    const familySlug = decodeURIComponent(blackFamilyTrain[1]!);
    if (isBlackBrowseFamilySlug(familySlug)) {
      return { kind: 'black-family', familySlug };
    }
  }

  if (pathname === '/courses/opening/b/train') {
    return { kind: 'opening-side', trainSide: 'b' };
  }

  const phaseTrain = /^\/courses\/(opening|middlegame|endgame|mistakes)\/train$/.exec(
    pathname,
  );
  if (phaseTrain) {
    return { kind: 'phase', phase: phaseTrain[1] as CoursePhase };
  }

  const courseTrain =
    /^\/courses\/(opening|middlegame|endgame|mistakes)\/([^/]+)\/train$/.exec(
      pathname,
    );
  if (courseTrain) {
    return {
      kind: 'course',
      phase: courseTrain[1] as CoursePhase,
      courseSlug: decodeURIComponent(courseTrain[2]),
    };
  }

  if (pathname === '/courses/train') {
    return { kind: 'global' };
  }

  return { kind: 'global' };
};

export const isCoursesPath = (pathname: string): boolean =>
  pathname === COURSES_ROOT || pathname.startsWith(`${COURSES_ROOT}/`);
